import { useCallback, useEffect, useRef, useState } from 'react';
import { getInstrument, isSampledInstrument } from '../utils/midiInstruments';
import {
  getDownloadedIds,
  isInstrumentLoaded,
  isInstrumentLoading,
  loadInstrumentBuffers,
  subscribeToProgress,
} from '../utils/sampleLibrary';

// Tone is loaded lazily here for the same reason useMidiPlayer does it — a page that never opens
// a MIDI modal should never pay for the library. Both share the module cache webpack gives an
// import(), so whichever asks first is the only one that downloads it.
const loadTone = () => import('tone');

const IDLE = { status: 'idle', loaded: 0, total: 0 };

/**
 * Progress for the recordings behind the selected instrument, and a way to start fetching them.
 *
 * Deliberately does not download on its own. Every instrument is a few hundred KB now, so merely
 * opening a part's MIDI modal must not spend the user's bandwidth on whatever happened to be
 * saved — the download is started by picking an instrument, or by pressing play. This hook only
 * watches, which is why it can show a bar for a download the play button started.
 *
 * @param {string} instrumentId - The currently selected preset
 * @param {boolean} isActive - Whether the picker is on screen. Both MIDI modals stay mounted while
 *   closed, so without this a closed modal would keep subscriptions alive for nothing.
 * @returns {{ status: 'idle'|'loading'|'ready'|'error', loaded: number, total: number,
 *   name: string, error: string, download: function, retry: function,
 *   downloadedIds: Set<string> }} The counts, name and message are what the song form's progress
 *   bar reads. The View player shows no bar — there, `status` alone is what the play button reads
 *   to say a download is running.
 */
const useSampleLoader = (instrumentId, isActive = false) => {
  const [state, setState] = useState(IDLE);
  const [downloadedIds, setDownloadedIds] = useState(() => new Set());
  const mountedRef = useRef(true);

  // The instrument download() was last asked for. The effect below re-runs the moment the picker
  // changes selection — before the library has an entry to report — so without this it would read
  // "nothing is happening here" and wipe out the loading state download() had just set.
  const requestedRef = useRef(null);

  const preset = getInstrument(instrumentId);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // What the picker needs to stop quoting a download size for something already downloaded. Read
  // when the modal opens and again whenever one finishes, which is the only time the set grows.
  useEffect(() => {
    if (!isActive) return;

    getDownloadedIds().then(
      (ids) => {
        if (mountedRef.current) setDownloadedIds(ids);
      },
      () => {},
    );
  }, [isActive, state.status]);

  // Reflect whatever is happening to this instrument, whoever set it off
  useEffect(() => {
    if (!isActive) {
      setState(IDLE);

      return;
    }

    // A synth preset has no files behind it, so there is never a download to report on
    if (!isSampledInstrument(getInstrument(instrumentId))) {
      setState({ status: 'ready', loaded: 0, total: 0 });

      return;
    }

    if (isInstrumentLoaded(instrumentId)) {
      setState({ status: 'ready', loaded: 0, total: 0 });

      return;
    }

    // Either a download is already running for this instrument — started by the picker, by play,
    // or by another modal — or this hook has just asked for one. Anything else is genuinely idle.
    const isPending = isInstrumentLoading(instrumentId) || requestedRef.current === instrumentId;

    setState((previous) =>
      isPending ? (previous.status === 'loading' ? previous : { status: 'loading', loaded: 0, total: 0 }) : IDLE,
    );

    return subscribeToProgress(instrumentId, (loaded, total, failed) => {
      if (!mountedRef.current) return;

      if (failed) {
        requestedRef.current = null;
        setState({ status: 'error', loaded: 0, total: 0 });

        return;
      }

      setState(loaded === total ? { status: 'ready', loaded, total } : { status: 'loading', loaded, total });
    });
  }, [instrumentId, isActive]);

  /**
   * Start the download for an instrument. Takes the id explicitly because the picker calls this
   * in the same breath as changing its selection, before the new value has reached this hook.
   */
  const download = useCallback(async (targetId) => {
    const id = targetId || instrumentId;

    // Synth presets play straight out of Tone. Falling through would hit loadInstrumentBuffers'
    // rejection for a non-sampled preset and put a download error on screen for an instrument
    // that is already ready to play.
    if (!isSampledInstrument(getInstrument(id))) {
      requestedRef.current = null;
      setState({ status: 'ready', loaded: 0, total: 0 });

      return;
    }

    if (isInstrumentLoaded(id)) return;

    requestedRef.current = id;
    setState({ status: 'loading', loaded: 0, total: 0 });

    try {
      const Tone = await loadTone();

      // Progress arrives through the subscription above, not from here — this only has to wait
      await loadInstrumentBuffers(Tone, id);

      if (mountedRef.current) setState({ status: 'ready', loaded: 0, total: 0 });
    } catch (error) {
      console.error('Instrument samples failed to download', error);

      // The song form's bar turns this into a retry. In View, where there is no bar, it only has
      // to get out of 'loading' — which would otherwise leave the play button disabled and
      // claiming to be downloading for the rest of the session.
      if (mountedRef.current) setState({ status: 'error', loaded: 0, total: 0 });
    }
  }, [instrumentId]);

  const retry = useCallback(() => download(instrumentId), [download, instrumentId]);

  return {
    ...state,
    name: preset?.name || '',
    error: state.status === 'error' ? `Could not download ${preset?.name || 'that instrument'}.` : '',
    download,
    retry,
    downloadedIds,
  };
};

export default useSampleLoader;
